/** @format */
import { ApprovalRequiredState, ModuleContentApprovalState, PlanValidationState } from '../../../types/baseTypes.js';
import { OtmId } from '../../../types/commonTypes.js';
import { Rule, WorkflowState } from '../../../types/generated/common-backend.js';
import { RuleContextResults } from './context/ruleContext.js';
import { RuleValidationResult, RuleValidationResultFor } from './ruleValidationResult.js';
import { ValidatablePlan } from './validatablePlan.js';

/** The validation result of the plan's root, the education or the module the plan is made for. */
export interface RootValidationResult extends RuleContextResults {
    validatablePlan: ValidatablePlan;
}

export interface CourseUnitValidationResult extends RuleContextResults {
    /** Set when the parent module has a module content approval: whether the approval leaves the course unit out. */
    invalidAccordingToModuleContentApproval?: boolean;
}

/** The module content approval fields are set only for the modules they concern, and an attained module's parts get none of the fields. */
export interface ModuleValidationResult extends RuleContextResults {
    /** Set when the parent module has a module content approval: whether the approval leaves the module out. */
    invalidAccordingToModuleContentApproval?: boolean;
    approvalRequiredState?: ApprovalRequiredState | null;
    moduleContentApprovalValidationState?: ModuleContentApprovalState;
    moduleContentApprovalState?: WorkflowState;
    isModuleContentApproved?: boolean;
    validationStateOfChildren?: PlanValidationState;
    hasInvalidOrMissingSelections?: boolean;
}

/** The validator reports a custom attainment only when its selection is invalid. */
export interface CustomAttainmentValidationResult {
    invalidSelection?: boolean;
    invalidAccordingToModuleContentApproval?: boolean;
}

export class PlanValidationResult {
    validatablePlan: ValidatablePlan | null = null;
    rootValidationResult: RootValidationResult | null = null;
    moduleValidationResults: { [id: string]: ModuleValidationResult } = {};
    courseUnitValidationResults: { [id: string]: CourseUnitValidationResult } = {};
    customModuleAttainmentValidationResults: { [id: string]: CustomAttainmentValidationResult } = {};
    customCourseUnitAttainmentValidationResults: { [id: string]: CustomAttainmentValidationResult } = {};
    ruleValidationResults: { [parentModuleId: string]: { [ruleLocalId: string]: RuleValidationResult } } = {};
    moduleIndexes: { [id: string]: number | null } = {};
    courseUnitIndexes: { [id: string]: number | null } = {};
    customModuleAttainmentIndexes: { [id: string]: number | null } = {};
    customCourseUnitAttainmentIndexes: { [id: string]: number | null } = {};
    matchingModuleContentApprovalsByModuleId: { [id: string]: any } = {};
}

/**
 * The validation result of `rule` under `parentModuleId`, typed by the rule type. The validator stores each rule's result under its
 * `localId`, so the result found there is always of the rule's own type.
 */
export function getRuleValidationResult<R extends Rule>(
    planValidationResult: PlanValidationResult | null | undefined,
    parentModuleId: OtmId,
    rule: R,
): RuleValidationResultFor<R> | undefined {
    return planValidationResult?.ruleValidationResults?.[parentModuleId]?.[rule?.localId] as RuleValidationResultFor<R> | undefined;
}
